import { Typography, Chip } from 'components/common'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'
import useCategoryInputModal from './useCategoryInputModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxArchive } from '@fortawesome/pro-regular-svg-icons'
import { useForumContext } from './ForumContext'
import { useCollectiveContext } from 'context/CollectiveContext'
import moment from 'moment-timezone'
import StyledTooltip from 'components/Tooltip/Tooltip'
import { faBell, faBellSlash } from '@fortawesome/pro-solid-svg-icons'
import {
  Category,
  useUpdateDiscussionBoardCategoryNotificationSettingMutation,
  useUpdateDiscussionBoardNotificationSettingMutation
} from 'graphql/generated'
import Spinner from 'components/Spinner'
import DropdownMenu, {
  MenuItemProps
} from 'components/common/Dropdown/DropdownMenu'
import { useMemo, useState } from 'react'
import BaseModal from 'components/modals/BaseModal'
import { useAnalyticsContext } from 'context/AnalyticsContext'

const Categories: React.FC = () => {
  const { collective } = useCollectiveContext()
  const { categories, selectedCategoryId, discussionBoard } = useForumContext()
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  const { modalTrigger: newCategoryTrigger } = useCategoryInputModal({})

  const [updateBoardNotification, { loading: isUpdatingNotification }] =
    useUpdateDiscussionBoardNotificationSettingMutation()

  const nonArchiveCategories = categories.filter(category => !category.archived)
  const mutedCategories = nonArchiveCategories.filter(
    category => !category.notificationSettings.allowNotifications
  )
  const allMuted = mutedCategories.length === nonArchiveCategories.length

  const [showMutedCategoriesModal, setShowMutedCategogiesModal] =
    useState(false)

  const notificationOptions = useMemo(() => {
    const options: MenuItemProps[] = [
      {
        icon: faBell,
        iconProps: { className: 'text-yellow-500' },
        type: 'button',
        label: 'Enable notifications for all categories',
        disabled:
          discussionBoard.notificationSettings?.allowGlobalNotifications,
        onClick: () =>
          updateBoardNotification({
            variables: {
              discussionBoardId: discussionBoard.id,
              allowGlobalNotifications: true
            },
            onCompleted: () => {
              if (analyticsLoaded) {
                window.analytics?.trackAll(
                  `Forum - Global Notification - Enable`
                )
              }
            }
          })
      },
      {
        icon: faBellSlash,
        type: 'button',
        label: 'Disable notifications for all categories',
        disabled:
          allMuted &&
          !discussionBoard.notificationSettings?.allowGlobalNotifications,
        onClick: () =>
          updateBoardNotification({
            variables: {
              discussionBoardId: discussionBoard.id,
              allowGlobalNotifications: false
            },
            onCompleted: () => {
              if (analyticsLoaded) {
                window.analytics?.trackAll(
                  `Forum - Global Notification - Disable`
                )
              }
            }
          })
      },
      {
        icon: faBell,
        type: 'button',
        label: 'See all category notifications',
        onClick: () => setShowMutedCategogiesModal(true)
      }
    ]

    return options
  }, [
    allMuted,
    analyticsLoaded,
    discussionBoard.id,
    discussionBoard.notificationSettings?.allowGlobalNotifications,
    updateBoardNotification
  ])

  return (
    <div className="sticky top-36 h-[calc(100vh-150px)] overflow-hidden">
      <div className="flex flex-col gap-4 h-full overflow-hidden">
        <div className="flex gap-8 justify-between items-center">
          <Typography size="sm" fontWeight="bold">
            Thread Categories
          </Typography>

          {collective.membership?.privileged && newCategoryTrigger}
        </div>

        <div className="flex-1 flex flex-col justify-between overflow-hidden">
          <div className="flex-1 pb-2 overflow-auto flex flex-col gap-2">
            {categories.map(curCategory => {
              return (
                <Link href={curCategory.publicUrl} key={curCategory.id}>
                  <div
                    className={twMerge(
                      'px-2 py-2 rounded-md flex justify-between items-center hover:bg-gray-200 gap-4',
                      curCategory.id === selectedCategoryId && 'bg-gray-100'
                    )}
                  >
                    <Typography size="sm" color="gray">
                      # {curCategory.name}
                    </Typography>

                    <div className="flex gap-2 items-center">
                      {curCategory.archived && (
                        <StyledTooltip title="Archived">
                          <FontAwesomeIcon
                            icon={faBoxArchive}
                            color="gray"
                            size="sm"
                          />
                        </StyledTooltip>
                      )}

                      {!!curCategory.totalThreads && (
                        <Chip size="xs" className="px-3">
                          {curCategory.totalThreads}
                        </Chip>
                      )}
                      {moment(curCategory.createdAt).isAfter(
                        moment().subtract(5, 'minutes')
                      ) && (
                        <Chip size="xs" className="px-3" fill="lightYellow">
                          New
                        </Chip>
                      )}
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>

          <div className="border-t border-gray-200 flex py-2 gap-1 items-center">
            {isUpdatingNotification ? (
              <span>
                <Spinner />
              </span>
            ) : (
              <DropdownMenu
                label="All Notifications"
                iconPlacement="before"
                icon={
                  discussionBoard.notificationSettings
                    .allowGlobalNotifications || !allMuted
                    ? faBell
                    : faBellSlash
                }
                iconProps={{
                  className:
                    discussionBoard.notificationSettings
                      ?.allowGlobalNotifications || !allMuted
                      ? 'text-yellow-500'
                      : 'text-gray-500'
                }}
                items={notificationOptions}
              />
            )}
          </div>

          {showMutedCategoriesModal && (
            <BaseModal onClose={() => setShowMutedCategogiesModal(false)}>
              <div className="flex-1 flex flex-col overflow-hidden py-4">
                <Typography size="heading" className="mx-4">
                  Category Notifications
                </Typography>

                <div className="flex-1 overflow-auto px-4">
                  {categories.map(category => (
                    <MutedCategoryListItem
                      key={category.id}
                      category={category}
                    />
                  ))}
                </div>
              </div>
            </BaseModal>
          )}
        </div>
      </div>
    </div>
  )
}

const MutedCategoryListItem: React.FC<{
  category: Pick<Category, 'name' | 'id' | 'notificationSettings'>
}> = ({ category }) => {
  const [updateNotification, { loading: isUpdatingCategoryNotification }] =
    useUpdateDiscussionBoardCategoryNotificationSettingMutation()
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  return (
    <div
      className="my-4 hover:bg-gray-50 p-2 rounded-md border border-gray-200 flex justify-between items-center"
      key={category.id}
    >
      <Typography>{category.name}</Typography>

      <div className="flex gap-2 items-center justify-center">
        {isUpdatingCategoryNotification ? (
          <span>
            <Spinner />
          </span>
        ) : (
          <FontAwesomeIcon
            icon={
              category.notificationSettings.allowNotifications
                ? faBell
                : faBellSlash
            }
            onClick={() =>
              updateNotification({
                variables: {
                  allowNotifications:
                    !category.notificationSettings.allowNotifications,
                  categoryId: category.id
                },
                onCompleted: () => {
                  if (analyticsLoaded) {
                    window.analytics?.trackAll(
                      `Forum - Category Notification - Update`,
                      {
                        modal: true,
                        allowNotifications:
                          !category.notificationSettings.allowNotifications,
                        categoryId: category.id
                      }
                    )
                  }
                }
              })
            }
            className={twMerge(
              'cursor-pointer text-gray-500 w-4',
              category.notificationSettings.allowNotifications &&
                'text-yellow-500'
            )}
          />
        )}
      </div>
    </div>
  )
}

export default Categories
